import { BaseCard } from '@/components/ui/base-card'
import { Slider } from '@/components/ui/slider'
import { useTargetedAreaWebsocket } from '@/hooks/useTargetedAreaWebsocket'
import { setSelectedReferenceField } from '@/redux/store/slices/map/mapSlice'
import { formatValueLegend } from '@/utils'
import { useAppDispatch } from '@redux/hooks/useAppState'
import { Trash2 } from 'lucide-react'
import { useMemo } from 'react'

import { usePercentileValues } from '../Grids/Map/hooks'

import type { ReferenceFields } from 'honeygrid-types'
import type { FC } from 'react'

interface CardWithSliderProps {
	title: string
	pendingValue: number
	selectionValue: number
	marks?: Array<{ value: number; label: string }>
	onRemove: () => void
	onChange: (newValue: number) => void
	onUpdateSelection: (newValue: number) => void
	onCardClick: () => void
	selected: boolean
	field: ReferenceFields
}

const CardWithSlider: FC<CardWithSliderProps> = ({
	title,
	selectionValue = 0,
	onRemove,
	onUpdateSelection,
	onChange,
	onCardClick,
	selected,
	field,
}) => {
	const dispatch = useAppDispatch()
	const { data } = useTargetedAreaWebsocket()

	const percentileValues = usePercentileValues(field, data?.aggregatedDemographics)

	const formattedPercentileValues = useMemo(() => {
		return percentileValues.map((value) => formatValueLegend(value, field))
	}, [field, percentileValues])

	const percentileMarks = useMemo(() => {
		return percentileValues.length > 0
			? [
					{ value: 0, label: formattedPercentileValues[0] },
					{ value: 50, label: formattedPercentileValues[49] },
					{ value: 100, label: formattedPercentileValues[99] },
				]
			: [
					{ value: 0, label: '0%' },
					{ value: 50, label: '50%' },
					{ value: 100, label: '100%' },
				]
	}, [formattedPercentileValues, percentileValues])

	return (
		<BaseCard
			className="w-full"
			title={title || 'Targeting rule'}
			isSelected={selected}
			isClickable={true}
			onClick={onCardClick}
			singleActionIcon={<Trash2 className="h-4 w-4" />}
			singleActionOnClick={onRemove}
			singleActionVariant="destructive"
			showActionsOnHover={true}
		>
			<div className="space-y-2">
				<div className="relative" onClick={(e) => e.stopPropagation()}>
					<Slider
						value={[selectionValue]}
						onValueChange={(values) => {
							onChange(values[0])
						}}
						onValueCommit={(values) => {
							onUpdateSelection(values[0])
						}}
						onPointerDown={(e) => {
							// Prevent card click when interacting with slider
							e.stopPropagation()
							if (!selected) {
								dispatch(setSelectedReferenceField(field))
								onChange(selectionValue)
							}
						}}
						max={100}
						step={1}
						className="relative"
						minStepsBetweenThumbs={0}
					/>
				</div>
				<div className="flex justify-between text-xs text-muted-foreground">
					{percentileMarks.map((mark) => (
						<span key={mark.value}>{mark.label}</span>
					))}
				</div>
			</div>
		</BaseCard>
	)
}

export { CardWithSlider }
export type { CardWithSliderProps }
